/**
 * InputManager — §3.1–§3.11 facade over touch (primary, AMD-1), keyboard+mouse and gamepad.
 *
 * `sample()` is called once per sim tick from `LoopHooks.sampleInput`, before `World.step`, so the
 * §3.11 criterion "synthetic event → state change ≤ 1 tick" holds by construction. Every buffer
 * window in this module counts sim ticks, never wall clock (§3.6, spine §7 determinism).
 *
 * The manager allocates nothing per sample. The one unavoidable exception is
 * `navigator.getGamepads()`, which allocates its result array inside the browser; it is only
 * reached when a pad is present.
 */

import { STEP } from '../core/loop';
import type { EventPayloads } from '../core/events';
import type { Vec2 } from '../sim/entities';
import type { InputState, World } from '../sim/world';
import { wrapDeltaX, wrapDeltaY } from '../sim/wrap';
import {
  ACTION_INDEX,
  BindingTable,
  loadInputSettings,
  saveInputSettings,
  type ActionId,
  type DeadzoneSettings,
  type TouchSettings,
} from './bindings';
import { cfg } from '../core/config';
import {
  FOLLOW_FULL_SPEED_PX,
  FOLLOW_STOP_RADIUS_PX,
  followWish,
  makeFollowView,
  pointerScale,
  type FollowView,
  type MovementScheme,
} from './follow';
import { GamepadDevice, applyRadialDeadzone, type VibrationEvent } from './gamepad';

/** Mirrors the audio warning's LOW_HP_ENTER; the §3.10 cooldown in `GamepadDevice` rate-limits it. */
const HAPTIC_LOW_HP_ENTER = 0.25;
import { KeyboardDevice } from './keyboard';
import { MouseDevice } from './mouse';
import { DOUBLE_TAP_MS, STICK_RADIUS_PX, TouchDevice, type TouchView } from './touch';

export * from './bindings';
export {
  FOLLOW_FULL_SPEED_PX,
  FOLLOW_STOP_RADIUS_PX,
  FOLLOW_SCALE_PROBE_PX,
  followWish,
  makeFollowView,
  pointerScale,
  type FollowView,
  type MovementScheme,
} from './follow';
export { applyRadialDeadzone, VIBRATION, type VibrationEvent, type VibrationSpec } from './gamepad';
export {
  DOUBLE_TAP_MS,
  DOUBLE_TAP_SLOP_MULT,
  DOUBLE_TAP_TICKS,
  STICK_RADIUS_PX,
  TAP_TICKS,
  TAP_TRAVEL_PX,
  type StickView,
  type TouchView,
} from './touch';

export type DeviceKind = 'touch' | 'keyboard' | 'gamepad';

/** §3.6 buffer windows in sim ticks: 150 ms and 200 ms at 60 Hz. */
export const DASH_BUFFER_TICKS = 9;
export const UI_BUFFER_TICKS = 12;

/** §3.5 gamepad UI nav repeat: first after 300 ms, then every 110 ms. */
export const NAV_REPEAT_FIRST_TICKS = 18;
export const NAV_REPEAT_TICKS = 7;

/** §3.8 gamepad aim-assist cone and slew ceiling. */
export const ASSIST_CONE_RAD = (10 * Math.PI) / 180;
export const ASSIST_MAX_RATE_RAD_S = (720 * Math.PI) / 180;

/** UI action codes emitted by `consumeUiAction()`. R8 drives menus off these. */
export const UI_NONE = 0;
export const UI_PAUSE = 1;
export const UI_CONFIRM = 2;
export const UI_BACK = 3;
export const UI_SELECT_1 = 4;
export const UI_SELECT_2 = 5;
export const UI_SELECT_3 = 6;
export const UI_REROLL = 7;
export const UI_RESTART = 8;
export const UI_NAV_UP = 9;
export const UI_NAV_DOWN = 10;
export const UI_NAV_LEFT = 11;
export const UI_NAV_RIGHT = 12;

/**
 * §3.8 aim assist hook. Given the current focus direction, write the best in-cone target's
 * direction into `out` and return true. R2's targeting supplies this; the manager applies the
 * ±10° cone gate and the 720°/s slew limit. Never moves a cursor.
 */
export type AimAssistProvider = (dirX: number, dirY: number, out: Vec2) => boolean;

/** 1-deep press buffer stamped with the sim tick it landed on (§3.6). */
export class TickBuffer {
  private pressedAt = -1;

  constructor(public windowTicks: number) {}

  press(tick: number): void {
    this.pressedAt = tick;
  }

  /** Live while inside the window; auto-expires. */
  live(now: number): boolean {
    if (this.pressedAt < 0) return false;
    if (now - this.pressedAt > this.windowTicks) {
      this.pressedAt = -1;
      return false;
    }
    return true;
  }

  /** Sim tick of the live press, or -1. */
  tickOf(now: number): number {
    return this.live(now) ? this.pressedAt : -1;
  }

  /** FIFO consume on the first tick the action is legal. */
  consume(now: number): boolean {
    if (!this.live(now)) return false;
    this.pressedAt = -1;
    return true;
  }

  clear(): void {
    this.pressedAt = -1;
  }
}

/**
 * §3.8 — rotate `from` toward `to` when the two are inside `cone`, capped at `maxRate` rad/s over
 * `dt` seconds. Outside the cone nothing moves: "No snap beyond ±10°".
 */
export function snapTowards(from: number, to: number, cone: number, maxRate: number, dt: number): number {
  let delta = to - from;
  while (delta > Math.PI) delta -= Math.PI * 2;
  while (delta < -Math.PI) delta += Math.PI * 2;
  if (Math.abs(delta) > cone) return from;
  const maxStep = maxRate * dt;
  const step = delta > maxStep ? maxStep : delta < -maxStep ? -maxStep : delta;
  return from + step;
}

const UI_QUEUE_CAP = 32;

const NAV_ACTIONS: readonly ActionId[] = ['move_up', 'move_down', 'move_left', 'move_right'];
const NAV_CODES: readonly number[] = [UI_NAV_UP, UI_NAV_DOWN, UI_NAV_LEFT, UI_NAV_RIGHT];

const MOVE_UP = 1 << ACTION_INDEX.move_up;
const MOVE_DOWN = 1 << ACTION_INDEX.move_down;
const MOVE_LEFT = 1 << ACTION_INDEX.move_left;
const MOVE_RIGHT = 1 << ACTION_INDEX.move_right;
const BIT_DASH = 1 << ACTION_INDEX.dash;
const BIT_PAUSE = 1 << ACTION_INDEX.pause;
const BIT_CONFIRM = 1 << ACTION_INDEX.ui_confirm;
const BIT_BACK = 1 << ACTION_INDEX.ui_back;
const BIT_REROLL = 1 << ACTION_INDEX.reroll;
const BIT_RESTART = 1 << ACTION_INDEX.restart;
const BIT_SELECT_1 = 1 << ACTION_INDEX.select_1;
const BIT_SELECT_2 = 1 << ACTION_INDEX.select_2;
const BIT_SELECT_3 = 1 << ACTION_INDEX.select_3;
const BIT_FOCUS = 1 << ACTION_INDEX.focus_aim;

// module-scope scratch — sample() must not allocate
const tmpA: Vec2 = { x: 0, y: 0 };
const tmpB: Vec2 = { x: 0, y: 0 };
const tmpStick: Vec2 = { x: 0, y: 0 };
const tmpAssist: Vec2 = { x: 0, y: 0 };

function noopDevice(_d: DeviceKind): void {
  /* replaced by the consumer */
}

export class InputManager {
  readonly bindings = new BindingTable();
  readonly touch: TouchDevice;
  readonly keyboard: KeyboardDevice;
  readonly mouse: MouseDevice;
  readonly gamepad: GamepadDevice;
  readonly touchView: TouchView;

  activeDevice: DeviceKind = 'keyboard';
  onDeviceChange: (d: DeviceKind) => void = noopDevice;

  /** §3.6 buffers. */
  readonly dashBuffer = new TickBuffer(DASH_BUFFER_TICKS);
  readonly pauseBuffer = new TickBuffer(UI_BUFFER_TICKS);

  /** §3.8 measurement hook — angular rate applied by aim assist on the last sample, deg/s. */
  lastAssistRateDegPerS = 0;

  private deadzone: DeadzoneSettings = { inner: 0.18, outer: 0.92 };
  private touchCfg: TouchSettings = {
    stick_radius: STICK_RADIUS_PX,
    tap_ms: 180,
    tap_slop: 12,
    double_tap_ms: DOUBLE_TAP_MS,
    left_handed: false,
    scheme: 'stick',
  };
  private tick = 0;
  private uiMode = false;
  private assist: AimAssistProvider | null = null;
  private playerX = 0;
  private playerY = 0;
  private focusAngle = 0;
  private focusHasAngle = false;

  /** Pointer-follow tuning, CSS px. Registry-backed so a designer can move it without a rebuild. */
  private followFullPx = cfg.get('input.follow_full_speed_px');
  private followStopPx = cfg.get('input.follow_stop_radius_px');
  /** Mouse follow: the world point under the cursor, resolved only when the cursor actually moves. */
  private readonly mouseFollow: FollowView = makeFollowView();

  private readonly uiCodes = new Int32Array(UI_QUEUE_CAP);
  private readonly uiTicks = new Int32Array(UI_QUEUE_CAP);
  private uiHead = 0;
  private uiTail = 0;

  private navHeld = new Int32Array(NAV_ACTIONS.length).fill(-1);
  private navNext = new Int32Array(NAV_ACTIONS.length).fill(-1);

  private disposed = false;

  constructor(target: HTMLElement) {
    loadInputSettings(this.bindings, this.deadzone, this.touchCfg);
    this.touch = new TouchDevice(target);
    this.keyboard = new KeyboardDevice(this.bindings);
    this.mouse = new MouseDevice(target, this.bindings);
    this.gamepad = new GamepadDevice(this.bindings);
    this.gamepad.setDeadzones(this.deadzone.inner, this.deadzone.outer);
    this.applyTouchConfig();
    this.touchView = this.touch.view;
    if (this.touchView.supported) this.activeDevice = 'touch';
  }

  private applyTouchConfig(): void {
    const t = this.touchCfg;
    this.touch.configure(t.stick_radius, t.tap_ms, t.tap_slop, t.left_handed, t.double_tap_ms);
    this.touch.setScheme(t.scheme);
  }

  /**
   * AMD-1 touch tuning; persisted with `persist()`. Leaves the movement scheme alone.
   * `leftHanded` no longer selects a half — roles go by arrival order — and is carried only so the
   * stored settings blob keeps its shape.
   */
  setTouchConfig(
    stickRadius: number,
    tapMs: number,
    tapSlop: number,
    leftHanded: boolean,
    doubleTapMs = this.touchCfg.double_tap_ms,
  ): void {
    this.touchCfg.stick_radius = stickRadius;
    this.touchCfg.tap_ms = tapMs;
    this.touchCfg.tap_slop = tapSlop;
    this.touchCfg.left_handed = leftHanded;
    this.touchCfg.double_tap_ms = doubleTapMs;
    this.applyTouchConfig();
  }

  /**
   * Touch movement scheme. `stick` is AMD-1's twin-stick (default); `follow` makes the whole
   * surface a pointer-follow, with Focus Aim on a second finger and dash on a tap by either.
   * Wire this to the settings UI; call `persist()` after to store it.
   *
   * Desktop mouse follow is not gated on this — LMB carries no in-run gameplay binding, so holding
   * it to steer takes nothing away, and keyboard or pad input always outranks it.
   */
  setMovementScheme(scheme: MovementScheme): void {
    if (scheme !== 'stick' && scheme !== 'follow') return;
    this.touchCfg.scheme = scheme;
    this.touch.setScheme(scheme);
  }

  get movementScheme(): MovementScheme {
    return this.touchCfg.scheme;
  }

  /**
   * Read-only follow target for the HUD — whichever pointer is driving, touch or mouse. `active`
   * is false when no pointer is steering, so R8 can draw one marker and ignore the device.
   */
  get followView(): FollowView {
    return this.touch.follow.view.active ? this.touch.follow.view : this.mouseFollow;
  }

  /** Pointer-follow feel, CSS px. Defaults come from the A.5 registry. */
  setFollowTuning(fullSpeedPx: number, stopRadiusPx: number): void {
    if (fullSpeedPx > 0) this.followFullPx = fullSpeedPx;
    if (stopRadiusPx >= 0) this.followStopPx = stopRadiusPx;
  }

  // --- configuration ---

  /** §3.5 radial dead zones, persisted into `settings.v1`. */
  setDeadzones(inner: number, outer: number): void {
    if (!(inner >= 0) || !(outer > inner) || outer > 1) return;
    this.deadzone.inner = inner;
    this.deadzone.outer = outer;
    this.gamepad.setDeadzones(inner, outer);
  }

  getDeadzones(out: DeadzoneSettings): DeadzoneSettings {
    out.inner = this.deadzone.inner;
    out.outer = this.deadzone.outer;
    return out;
  }

  /** Persists bindings + dead zones + touch tuning into `lumenabyss.settings.v1` (A.6). */
  persist(): void {
    saveInputSettings(this.bindings, this.deadzone, this.touchCfg);
  }

  /** Re-reads `settings.v1`; used after a remap screen cancel. */
  reload(): void {
    loadInputSettings(this.bindings, this.deadzone, this.touchCfg);
    this.gamepad.setDeadzones(this.deadzone.inner, this.deadzone.outer);
    this.applyTouchConfig();
  }

  /** §3.9 restore defaults + persist. */
  restoreDefaults(): void {
    this.bindings.restoreDefaults();
    this.deadzone.inner = 0.18;
    this.deadzone.outer = 0.92;
    this.touchCfg.stick_radius = STICK_RADIUS_PX;
    this.touchCfg.tap_ms = 180;
    this.touchCfg.tap_slop = 12;
    this.touchCfg.double_tap_ms = cfg.get('input.double_tap_ms');
    this.touchCfg.left_handed = false;
    this.touchCfg.scheme = 'stick';
    this.followFullPx = cfg.get('input.follow_full_speed_px');
    this.followStopPx = cfg.get('input.follow_stop_radius_px');
    this.gamepad.setDeadzones(0.18, 0.92);
    this.applyTouchConfig();
    this.persist();
  }

  setAimAssistProvider(fn: AimAssistProvider | null): void {
    this.assist = fn;
  }

  /** The sim's player position, needed for the mouse focus vector player→cursor (§3.3). */
  setPlayerWorldPos(x: number, y: number): void {
    this.playerX = x;
    this.playerY = y;
  }

  /** Menus on: nav repeat events are emitted; menus off: only discrete UI actions. */
  setUiMode(on: boolean): void {
    this.uiMode = on;
    if (!on) {
      this.navHeld.fill(-1);
      this.navNext.fill(-1);
    }
  }

  setHapticsEnabled(on: boolean): void {
    this.gamepad.hapticsEnabled = on;
  }

  get currentTick(): number {
    return this.tick;
  }

  // --- per-tick sampling ---

  /**
   * Writes the sim-visible input state. `cameraToWorld` maps a CSS-px screen point on the game
   * surface to a world-space point; the manager uses two mapped points for touch drag so a camera
   * transform is honoured without the input layer knowing the projection.
   */
  sample(out: InputState, cameraToWorld: (sx: number, sy: number, out: Vec2) => void): void {
    const tick = this.tick;
    this.touch.setTick(tick);
    this.keyboard.setTick(tick);
    this.mouse.setTick(tick);
    this.gamepad.poll(tick);

    this.updateDevice();

    const kbPressed = this.keyboard.drainPressed();
    const msPressed = this.mouse.drainPressed();
    const padPressed = this.gamepad.drainPressed();
    const pressed = kbPressed | msPressed | padPressed;
    const held = this.keyboard.heldMask | this.mouse.heldMask | this.gamepad.heldMask;

    this.resolveFollowAnchors(cameraToWorld);
    this.sampleMove(out, cameraToWorld);
    this.sampleFocus(out, cameraToWorld, held);
    this.sampleActions(pressed);
    if (this.uiMode) this.sampleNav(held);

    if (this.touch.consumeDashTap()) this.dashBuffer.press(tick);
    if (pressed & BIT_DASH) this.dashBuffer.press(tick);
    const dashTick = this.dashBuffer.tickOf(tick);
    out.dashPressed = dashTick >= 0;
    out.dashBufferedTick = dashTick;

    this.tick = (tick + 1) >>> 0;
  }

  /** Call after the sim consumes a dash so a stale buffer cannot re-fire (optional). */
  clearDashBuffer(): void {
    this.dashBuffer.clear();
  }

  /**
   * Turns each live follow pointer's screen point into a world point, once, on the tick after it
   * lands or moves.
   *
   * This is the whole reason the ship arrives instead of chasing: the camera tracks the player, so
   * a stationary finger holds a stationary *screen offset*, and re-reading it every tick would be a
   * stick that never runs out. Resolved once and held, it is a place in the arena.
   */
  private resolveFollowAnchors(cameraToWorld: (sx: number, sy: number, out: Vec2) => void): void {
    const f = this.touch.follow;
    if (f.view.active && f.dirty) {
      cameraToWorld(f.view.x, f.view.y, tmpA);
      f.view.worldX = tmpA.x;
      f.view.worldY = tmpA.y;
      f.dirty = false;
    }

    // LMB is UI confirm, so a menu click must never steer the ship.
    const m = this.mouseFollow;
    if (!this.mouse.leftDown || this.uiMode) {
      m.active = false;
      m.magnitude = 0;
      m.dirX = 0;
      m.dirY = 0;
      return;
    }
    const x = this.mouse.x;
    const y = this.mouse.y;
    if (m.active && x === m.x && y === m.y) return;
    cameraToWorld(x, y, tmpA);
    m.worldX = tmpA.x;
    m.worldY = tmpA.y;
    m.x = x;
    m.y = y;
    m.active = true;
  }

  /**
   * Player → world target as a §3.2 wish, written into `out` and mirrored into `view` for the HUD.
   * Inside the stop radius this writes a zero wish, which is the settle.
   *
   * Thresholds are declared in CSS px and converted through the caller's own projection, so the
   * gesture keeps its size on a 390 px phone and a 1920 px desktop alike.
   */
  private applyFollow(
    out: InputState,
    cameraToWorld: (sx: number, sy: number, out: Vec2) => void,
    view: FollowView,
  ): void {
    const scale = pointerScale(cameraToWorld, tmpA, tmpB);
    // AMD-24: the arena is a torus. A raw `target − player` sends the ship the long way round the
    // moment it crosses the seam under a held pointer.
    const m = followWish(
      wrapDeltaX(view.worldX - this.playerX),
      wrapDeltaY(view.worldY - this.playerY),
      this.followFullPx * scale,
      this.followStopPx * scale,
      this.deadzone.inner,
      this.deadzone.outer,
      tmpStick,
    );
    view.magnitude = m;
    const inv = m > 0 ? 1 / m : 0;
    view.dirX = tmpStick.x * inv;
    view.dirY = tmpStick.y * inv;
    // already world-space, y up — no screen flip here, unlike the stick
    out.moveX = tmpStick.x;
    out.moveY = tmpStick.y;
  }

  private sampleMove(out: InputState, cameraToWorld: (sx: number, sy: number, out: Vec2) => void): void {
    // touch first (AMD-1: primary path), then gamepad analog, then keyboard digital
    const ms = this.touch.moveStick;
    if (ms.view.active) {
      const r = this.touch.stickRadius;
      const m = applyRadialDeadzone(
        ms.rawX / r,
        ms.rawY / r,
        this.deadzone.inner,
        this.deadzone.outer,
        tmpStick,
      );
      ms.view.magnitude = m;
      const inv = m > 0 ? 1 / m : 0;
      ms.view.dirX = tmpStick.x * inv;
      ms.view.dirY = tmpStick.y * inv;
      out.moveX = tmpStick.x;
      out.moveY = -tmpStick.y; // screen y grows down, world y grows up
      if (m > 0) return;
    }
    const follow = this.touch.follow.view;
    if (follow.active) {
      // a held follow thumb owns the wish, settled or not — falling through to another device
      // mid-gesture would fight the finger on a hybrid device
      this.applyFollow(out, cameraToWorld, follow);
      return;
    }
    if (this.gamepad.connected && this.gamepad.moveMagnitude > 0) {
      out.moveX = this.gamepad.move.x;
      out.moveY = -this.gamepad.move.y;
      return;
    }
    const held = this.keyboard.heldMask;
    let dx = 0;
    let dy = 0;
    if (held & MOVE_LEFT) dx -= 1;
    if (held & MOVE_RIGHT) dx += 1;
    if (held & MOVE_DOWN) dy -= 1;
    if (held & MOVE_UP) dy += 1;
    if (dx !== 0 && dy !== 0) {
      const inv = Math.SQRT1_2;
      dx *= inv;
      dy *= inv;
    }
    if (dx !== 0 || dy !== 0) {
      out.moveX = dx;
      out.moveY = dy;
      return;
    }
    // mouse follow last: an explicit key or stick always outranks a held button
    if (this.mouseFollow.active) {
      this.applyFollow(out, cameraToWorld, this.mouseFollow);
      return;
    }
    out.moveX = 0;
    out.moveY = 0;
  }

  private sampleFocus(
    out: InputState,
    cameraToWorld: (sx: number, sy: number, out: Vec2) => void,
    held: number,
  ): void {
    this.lastAssistRateDegPerS = 0;
    const aim = this.touch.aimStick;
    if (aim.view.active) {
      const r = this.touch.stickRadius;
      const m = applyRadialDeadzone(
        aim.rawX / r,
        aim.rawY / r,
        this.deadzone.inner,
        this.deadzone.outer,
        tmpStick,
      );
      aim.view.magnitude = m;
      const invM = m > 0 ? 1 / m : 0;
      aim.view.dirX = tmpStick.x * invM;
      aim.view.dirY = tmpStick.y * invM;
      if (m > 0) {
        cameraToWorld(aim.view.originX, aim.view.originY, tmpA);
        cameraToWorld(aim.view.curX, aim.view.curY, tmpB);
        if (this.writeDir(out, tmpB.x - tmpA.x, tmpB.y - tmpA.y)) {
          out.focusHeld = true;
          return;
        }
      }
    }

    if (held & BIT_FOCUS && this.mouse.heldMask & BIT_FOCUS) {
      cameraToWorld(this.mouse.x, this.mouse.y, tmpA);
      if (this.writeDir(out, tmpA.x - this.playerX, tmpA.y - this.playerY)) {
        out.focusHeld = true;
        return;
      }
    }

    if (this.gamepad.connected && (this.gamepad.focusEngaged || (this.gamepad.heldMask & BIT_FOCUS) !== 0)) {
      const gx = this.gamepad.aim.x;
      const gy = -this.gamepad.aim.y;
      if (gx !== 0 || gy !== 0) {
        this.applyAimAssist(gx, gy);
        out.focusHeld = true;
        out.focusX = Math.cos(this.focusAngle);
        out.focusY = Math.sin(this.focusAngle);
        return;
      }
    }

    out.focusHeld = false;
  }

  private writeDir(out: InputState, dx: number, dy: number): boolean {
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d <= 1e-6) return false;
    out.focusX = dx / d;
    out.focusY = dy / d;
    this.focusAngle = Math.atan2(dy, dx);
    this.focusHasAngle = true;
    return true;
  }

  /**
   * §3.8 — gamepad-only targeting bias. Snaps the focus direction toward the best in-cone target
   * within ±10°, capped at 720°/s. Never touches a cursor and never moves the stick.
   */
  private applyAimAssist(gx: number, gy: number): void {
    let ang = Math.atan2(gy, gx);
    if (this.assist) {
      tmpAssist.x = 0;
      tmpAssist.y = 0;
      if (this.assist(Math.cos(ang), Math.sin(ang), tmpAssist)) {
        const tx = tmpAssist.x;
        const ty = tmpAssist.y;
        if (tx !== 0 || ty !== 0) {
          const next = snapTowards(ang, Math.atan2(ty, tx), ASSIST_CONE_RAD, ASSIST_MAX_RATE_RAD_S, STEP);
          this.lastAssistRateDegPerS = (Math.abs(next - ang) / STEP) * (180 / Math.PI);
          ang = next;
        }
      }
    }
    this.focusAngle = ang;
    this.focusHasAngle = true;
  }

  private sampleActions(pressed: number): void {
    if (pressed === 0) return;
    const t = this.tick;
    if (pressed & BIT_PAUSE) {
      this.pauseBuffer.press(t);
      this.pushUi(UI_PAUSE, t);
    }
    if (pressed & BIT_CONFIRM) this.pushUi(UI_CONFIRM, t);
    if (pressed & BIT_BACK) this.pushUi(UI_BACK, t);
    if (pressed & BIT_SELECT_1) this.pushUi(UI_SELECT_1, t);
    if (pressed & BIT_SELECT_2) this.pushUi(UI_SELECT_2, t);
    if (pressed & BIT_SELECT_3) this.pushUi(UI_SELECT_3, t);
    if (pressed & BIT_REROLL) this.pushUi(UI_REROLL, t);
    if (pressed & BIT_RESTART) this.pushUi(UI_RESTART, t);
  }

  /** §3.5 — menu repeat: first after 300 ms, then every 110 ms. */
  private sampleNav(held: number): void {
    const t = this.tick;
    let padDigital = 0;
    if (this.gamepad.connected) {
      const m = this.gamepad.move;
      if (m.y < -0.5) padDigital |= MOVE_UP;
      else if (m.y > 0.5) padDigital |= MOVE_DOWN;
      if (m.x < -0.5) padDigital |= MOVE_LEFT;
      else if (m.x > 0.5) padDigital |= MOVE_RIGHT;
    }
    const all = held | padDigital;
    for (let i = 0; i < NAV_ACTIONS.length; i++) {
      const bit = 1 << ACTION_INDEX[NAV_ACTIONS[i]];
      if ((all & bit) === 0) {
        this.navHeld[i] = -1;
        this.navNext[i] = -1;
        continue;
      }
      if (this.navHeld[i] < 0) {
        this.navHeld[i] = t;
        this.navNext[i] = t + NAV_REPEAT_FIRST_TICKS;
        this.pushUi(NAV_CODES[i], t);
      } else if (t >= this.navNext[i]) {
        this.navNext[i] = t + NAV_REPEAT_TICKS;
        this.pushUi(NAV_CODES[i], t);
      }
    }
  }

  private pushUi(code: number, tick: number): void {
    const next = (this.uiTail + 1) % UI_QUEUE_CAP;
    if (next === this.uiHead) this.uiHead = (this.uiHead + 1) % UI_QUEUE_CAP; // drop oldest
    this.uiCodes[this.uiTail] = code;
    this.uiTicks[this.uiTail] = tick;
    this.uiTail = next;
  }

  /**
   * FIFO drain of buffered UI actions. Entries older than the §3.6 200 ms window are discarded,
   * so a stale confirm never fires on a screen that opened later.
   */
  consumeUiAction(): number {
    const now = this.tick;
    while (this.uiHead !== this.uiTail) {
      const code = this.uiCodes[this.uiHead];
      const at = this.uiTicks[this.uiHead];
      this.uiHead = (this.uiHead + 1) % UI_QUEUE_CAP;
      if (now - at <= UI_BUFFER_TICKS) return code;
    }
    return UI_NONE;
  }

  clearUiActions(): void {
    this.uiHead = 0;
    this.uiTail = 0;
  }

  // --- haptics (§3.10) ---

  vibrate(low: number, high: number, durationMs: number): void {
    this.gamepad.vibrate(low, high, durationMs);
  }

  /** Table-driven rumble with §3.10 preemption (player hit wins). */
  vibrateEvent(event: VibrationEvent): void {
    this.gamepad.vibrateEvent(event);
  }

  /**
   * §3.10 cues, driven by the run's own events the same way `AudioEngine.bindWorld` drives
   * cues: subscribe once per run, unsubscribe on rebind. The table's priorities and the
   * low-HP cooldown live in `GamepadDevice`; this only maps bus events onto table rows.
   * On touch the same rows reach `navigator.vibrate` through the gesture-unlock flag.
   */
  bindWorld(w: World): void {
    for (let i = 0; i < this.hapticUnsubs.length; i++) this.hapticUnsubs[i]();
    this.hapticUnsubs.length = 0;
    const bus = w.bus;
    const u = this.hapticUnsubs;
    u.push(
      bus.on('player_hit', (p: EventPayloads['player_hit']) => {
        this.vibrateEvent('player_hit');
        // Same enter threshold the audio warning uses (LOW_HP_ENTER 0.25).
        if (w.player.max_hp > 0 && p.hp_after / w.player.max_hp < HAPTIC_LOW_HP_ENTER) {
          this.vibrateEvent('low_hp');
        }
      }),
    );
    u.push(
      bus.on('dash', (p: EventPayloads['dash']) => {
        if (p.phase === 'start') this.vibrateEvent('dash');
      }),
    );
    u.push(bus.on('level_up', () => this.vibrateEvent('level_up')));
    u.push(
      bus.on('death', (p: EventPayloads['death']) => {
        if (p.elite) this.vibrateEvent('elite_kill');
      }),
    );
    u.push(bus.on('boss_spawn', () => this.vibrateEvent('boss_event')));
    u.push(bus.on('boss_phase', () => this.vibrateEvent('boss_event')));
    u.push(bus.on('boss_defeated', () => this.vibrateEvent('boss_event')));
  }

  private hapticUnsubs: Array<() => void> = [];

  // --- lifecycle ---

  private updateDevice(): void {
    let next: DeviceKind | null = null;
    if (this.touch.activity) {
      next = 'touch';
      this.touch.activity = false;
      this.gamepad.vibrateUnlocked = true;
    }
    if (this.gamepad.activity) {
      this.gamepad.activity = false;
      if (next === null) next = 'gamepad';
    }
    if (this.keyboard.activity || this.mouse.activity) {
      this.keyboard.activity = false;
      this.mouse.activity = false;
      if (next === null) next = 'keyboard';
    }
    if (next !== null && next !== this.activeDevice) {
      this.activeDevice = next;
      this.onDeviceChange(next);
    }
  }

  /** Drops every held state; used on pause and focus loss. */
  releaseAll(): void {
    this.touch.releaseAll();
    this.keyboard.releaseAll();
    this.mouse.releaseAll();
    this.mouseFollow.active = false;
    this.mouseFollow.magnitude = 0;
    this.dashBuffer.clear();
    this.pauseBuffer.clear();
    this.clearUiActions();
    this.navHeld.fill(-1);
    this.navNext.fill(-1);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.touch.dispose();
    this.keyboard.dispose();
    this.mouse.dispose();
    this.onDeviceChange = noopDevice;
  }
}
